/**
 * SignupPage.tsx — /app/signup registration form
 *
 * Purpose:    Public sign-up page for the ɳSentry SaaS app zone. Free tier
 *             requires no card — 10 monitors @ 5-minute checks.
 * Inputs:     email + password; auth.signUp from @/lib/api (gateway signup
 *             sets the session cookie — signup lands signed-in)
 * Outputs:    Session cookie on success → redirect to /app/welcome (onboarding)
 * Constraints: Dark-first styling per app convention; dashboard-zone pages
 *              hardcode English (no i18n, matching existing dashboard pages).
 * SOT:        nsentry SaaS app zone
 */
import { useState, type FormEvent } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { auth } from '@/lib/api'
import { useAuth } from '@/lib/auth-context'

export function SignupPage() {
  const navigate = useNavigate()
  const { refetch } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    if (!agreed) {
      setError('Please agree to the Terms of Service and Privacy Policy to continue')
      return
    }
    setLoading(true)
    setError(null)

    // Signup proxies the SaaS gateway (/v1/signup) and sets the session
    // cookie from the returned JWT — no separate sign-in round-trip needed.
    const signupResult = await auth.signUp(email, password)
    if (signupResult.error) {
      setError(signupResult.error.message)
      setLoading(false)
      return
    }

    await refetch()
    // First signup → guided onboarding (create a monitor, optional status
    // page, optional team invite). Everything in it is skippable.
    navigate('/app/welcome')
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg px-4 py-12">
      <div className="w-full max-w-sm">
        <div className="mb-8 text-center">
          <Link to="/" className="text-2xl font-bold text-sky-700 dark:text-sky-400">
            ɳSentry
          </Link>
          <h1 className="mt-4 text-2xl font-bold text-brand-ink">Get started free</h1>
          <p className="mt-1 text-sm text-brand-muted">
            Free tier: 10 monitors @ 5-minute checks — no card required
          </p>
        </div>

        <div className="rounded-2xl border border-brand-subtle bg-brand-surface p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <div className="rounded-lg bg-red-50 dark:bg-red-950/40 px-4 py-3" role="alert">
                <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
              </div>
            )}

            <div>
              <label htmlFor="email" className="mb-1 block text-sm font-medium text-brand-secondary">
                Email
              </label>
              <input
                id="email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="mb-1 block text-sm font-medium text-brand-secondary">
                Password
              </label>
              <input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-lg border border-brand-card bg-brand-surface-2 px-3 py-2 text-sm text-brand-ink placeholder-gray-500 focus:border-sky-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                placeholder="8+ characters"
              />
            </div>

            <div className="flex items-start gap-2.5">
              <input
                id="agree"
                type="checkbox"
                required
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-gray-600 bg-brand-surface-2 accent-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400"
              />
              <label htmlFor="agree" className="text-xs leading-relaxed text-brand-muted">
                I agree to the{' '}
                <Link to="/legal/terms" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/legal/privacy" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <button
              type="submit"
              disabled={loading || !agreed}
              className="w-full rounded-lg bg-sky-700 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-sky-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-sky-400 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? 'Creating account…' : 'Create account'}
            </button>
          </form>

          <p className="mt-6 text-center text-sm text-brand-muted">
            Already have an account?{' '}
            <Link to="/app/login" className="font-medium text-sky-700 dark:text-sky-400 hover:text-sky-700 dark:text-sky-300">
              Sign in
            </Link>
          </p>
        </div>
      </div>
    </div>
  )
}
